/**
 * What element a note is actually pointing at.
 *
 * Coordinates tell you *where* on a page a client clicked. They do not tell an
 * agent — or a designer reading the note a week later — *which component* to
 * change. This is the bridge: the element under the annotation, described in
 * terms you can grep a repository for.
 *
 * `text` is the highest-value field by a wide margin. A selector locates a node
 * in a DOM that no longer exists; the visible copy usually appears verbatim in
 * the source.
 */

export interface TargetRect {
  x: number
  y: number
  w: number
  h: number
}

export interface NoteTarget {
  /** Structural selector, unique in the captured document where possible. */
  selector: string
  tag: string
  id: string | null
  classes: string[]
  /** Grep-friendly attributes only: data-*, role, aria-label, alt, href, name. */
  attrs: Record<string, string>
  /** Visible text of the element, collapsed and truncated. */
  text: string | null
  /** Bounding box in document pixels, same space as the annotation itself. */
  rect: TargetRect
  /** A small, deliberately chosen slice of computed style. */
  styles: Record<string, string>
}

/** Caps applied on both sides so a hostile or runaway page cannot bloat a note. */
export const TARGET_LIMITS = {
  maxTargets: 8,
  maxSelector: 500,
  maxText: 400,
  maxClasses: 30,
  maxClassLength: 120,
  maxAttrs: 12,
  maxAttrLength: 300,
} as const

/**
 * Computed properties worth keeping. Chosen because feedback is usually about
 * one of them — "make it bolder", "too small", "wrong colour", "move it left".
 */
export const CAPTURED_STYLES = [
  'display',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'lineHeight',
  'color',
  'backgroundColor',
  'textAlign',
] as const
